import React, {useMemo, useState} from 'react'
import {useOutletContext, useRouteLoaderData} from '@remix-run/react'

import clsx from 'clsx'
import {getTranslation} from '~/localization/translation'
import type {RootAndListContext, RootLoaderResponse} from '~/types'
import {IconCalendar} from '~/icons/brg-icon-calendar'
import {useReportCustomEvent, useReportWidgetClick} from '~/hooks/analytic-hooks'

import ShareMenu from '~/components/commons/share-menu'

import {getDomainConfig} from '~/domains'
import {PointerIconArrow} from '~/icons/pointer-icon-arrow'
import {getUtcDateDaysAgo} from '~/utils/commons'
import Breadcrumbs from '../breadcrumbs/breadcrumbs'

const ProductsAboveTheListHeader = () => {
  const {
    dataResponse: {productsCategory, products},
  } = useOutletContext<RootAndListContext>()
  const {language, deviceType, featureFlags, domain, isUSLocale, locale, renderTimestamp} = useRouteLoaderData(
    'root',
  ) as RootLoaderResponse
  const translations = getTranslation(language)
  const {styles: stylesBydomain, domainType} = getDomainConfig(domain, locale)
  const [onHoverOnceReportedElements, setOnHoverOnceReportedElements] = useState<string[]>([])
  const reportWidgetClick = useReportWidgetClick()
  const reportCustomEvent = useReportCustomEvent()
  const productsCount = products.slice(0, 10).length
  const [fallbackRenderTimestamp] = useState(() => Date.now())

  const dateFormatter = useMemo(
    () => ({
      short: new Intl.DateTimeFormat(language, {month: 'short', day: '2-digit', year: 'numeric', timeZone: 'UTC'}),
      long: new Intl.DateTimeFormat(language, {month: 'long', day: '2-digit', year: 'numeric', timeZone: 'UTC'}),
    }),
    [language],
  )

  const formatDate = (timestamp: number, monthLength: 'long' | 'short' = 'long') => {
    const date = new Date(timestamp)
    return dateFormatter[monthLength].format(date)
  }

  const baseRenderTimestamp = Number.isFinite(renderTimestamp) ? renderTimestamp : fallbackRenderTimestamp
  const twoDaysAgo = getUtcDateDaysAgo(baseRenderTimestamp, 2)
  const twoDaysAgoMHY = formatDate(twoDaysAgo.getTime(), 'long')
  const twoDaysAgoMHYShort = formatDate(twoDaysAgo.getTime(), 'short')

  const onHover = (elementName: string, variation: string) => {
    if (deviceType === 'desktop' && !onHoverOnceReportedElements.includes(variation)) {
      reportCustomEvent('hover', {
        elementName,
        placement: 'top',
        variation,
      })
      setOnHoverOnceReportedElements([...onHoverOnceReportedElements, variation])
    }
  }

  const defaultTitle = isUSLocale
    ? `${translations.Header.titleFormat(productsCount, productsCategory?.name, featureFlags?.showEngagingElements)}`
    : `${translations.Header.best(productsCount, productsCategory?.gender)} ${productsCategory?.name}`

  const dealsTitle = `${translations.Header.titleDealsFormat(productsCount, productsCategory?.name)}`

  return (
    <div
      className={clsx(
        'mb-10 flex w-full flex-col border-b border-neutral-200 py-4 shadow-sm',
        featureFlags?.showNewHeaderAndCookieUi && deviceType !== 'desktop' ? 'mb-4' : '',
      )}
      data-testid="list-header"
    >
      <div
        className={clsx(
          'utils_main_container mx-auto flex w-full flex-col justify-center',
          stylesBydomain.UTILS_MAIN_CONTAINER,
        )}
      >
        <div className={clsx(isUSLocale ? 'h-6' : 'hidden')}>{isUSLocale && <Breadcrumbs />}</div>

        <div className="relative flex w-full flex-row justify-between">
          <div className="relative flex w-full flex-col justify-between">
            <h1 data-testid="category-title" className="mt-2 text-3xl font-[500] text-black mobile:text-2xl">
              {domainType === 'deals' ? dealsTitle : defaultTitle}
            </h1>
            {featureFlags?.showNewHeaderAndCookieUi && deviceType !== 'desktop' ? null : (
              <span className="text-base mobile:max-w-64 mobile:text-sm" data-testid="category-subtitle">
                {translations.Header.aboveTheListSubheader}
              </span>
            )}

            <div className="flex w-full items-center justify-between">
              <div
                data-testid="last-updated"
                className={clsx(
                  'mt-1 flex flex-row items-center gap-2',
                  'mobile:flex-col mobile:items-start mobile:gap-1',
                  featureFlags?.showNewHeaderAndCookieUi && deviceType !== 'desktop' ? 'mt-0' : 'mobile:mt-2',
                )}
              >
                <div
                  className="flex flex-row items-center gap-2"
                  aria-hidden="true"
                  onClick={() => {
                    reportWidgetClick({
                      widget_name: 'info-header',
                      widget_place: 'top',
                      trigger_type: 'last-updated',
                    })
                  }}
                  onMouseEnter={() => onHover('info-header', 'last-updated')}
                >
                  <IconCalendar />
                  {featureFlags?.showNewHeaderAndCookieUi && deviceType !== 'desktop' ? (
                    <span className="text-sm font-light text-[#4F4F4F]">
                      {translations.Header.updatedText}: {twoDaysAgoMHYShort}
                    </span>
                  ) : (
                    <span className="text-sm font-light text-[#4F4F4F]">{twoDaysAgoMHY}</span>
                  )}
                </div>
              </div>
              <div className="hidden items-center gap-4 tablet:flex">
                <ShareMenu onHover={onHover} className="h-9" placement="brg_above_the_list" shouldShowNewIcon={true} />
              </div>
            </div>
            <div className="mt-2 flex flex-row gap-1">
              <PointerIconArrow className="flex-shrink-0" />
              <span className="text-sm font-light !text-[#666666] tablet:text-xs">
                {translations.Header.disclaimer}
              </span>
            </div>
          </div>

          <div className="flex flex-col tablet:hidden">
            <ShareMenu
              onHover={onHover}
              className="mt-8 flex flex-row-reverse gap-5 mobile:mt-4"
              placement="brg_above_the_list"
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default ProductsAboveTheListHeader
